import React from 'react';
import {
  Box,
  Grid,
  Card,
  CardBody,
  Heading,
  Text,
  VStack,
  HStack,
  Badge,
  Divider,
  Progress
} from '@chakra-ui/react';
import { TrendingUp, DollarSign, FileText, Calendar, Target, BarChart3 } from 'lucide-react';

interface RevenueCardProps {
  title: string;
  value: string;
  change: string;
  changeType: 'positive' | 'negative';
  icon: any;
  colorScheme: string;
}

const RevenueCard: React.FC<RevenueCardProps> = ({ 
  title, 
  value, 
  change, 
  changeType,
  icon: Icon, 
  colorScheme 
}) => (
  <Card bg="gray.800" borderColor="gray.700">
    <CardBody>
      <HStack justify="space-between" mb={4}>
        <VStack align="start" spacing={1}>
          <Text fontSize="sm" color="gray.400" fontWeight="medium">
            {title}
          </Text>
          <Text fontSize="2xl" fontWeight="bold" color="white">
            {value}
          </Text>
        </VStack>
        <Box
          p={3}
          bg={`${colorScheme}.900`}
          borderRadius="lg"
          border="1px"
          borderColor={`${colorScheme}.700`}
        >
          <Icon size={24} color={`var(--chakra-colors-${colorScheme}-400)`} />
        </Box>
      </HStack>
      <HStack>
        <Text 
          fontSize="sm" 
          color={changeType === 'positive' ? 'green.400' : 'red.400'}
          fontWeight="medium"
        >
          {changeType === 'positive' ? '↗' : '↘'} {change}
        </Text>
        <Text fontSize="sm" color="gray.500">vs last month</Text>
      </HStack>
    </CardBody>
  </Card>
);

const Revenue: React.FC = () => {
  // Mock data - in real app, this would come from API
  const revenueData = {
    totalRevenue: '$47,250',
    monthlyGrowth: '12.5%',
    paidInvoices: '$42,100',
    pendingInvoices: '$8,450',
    averageInvoice: '$3,180',
    clientCount: 15
  };

  const monthlyBreakdown = [
    { month: 'Jan', revenue: 28500, invoices: 12 },
    { month: 'Feb', revenue: 31200, invoices: 14 },
    { month: 'Mar', revenue: 29800, invoices: 13 },
    { month: 'Apr', revenue: 35600, invoices: 16 },
    { month: 'May', revenue: 42100, invoices: 18 },
    { month: 'Jun', revenue: 47250, invoices: 21 }
  ];

  const topClients = [
    { name: 'TechStart Solutions', revenue: 12500, invoices: 4, status: 'paid' },
    { name: 'Digital Dynamics', revenue: 9800, invoices: 3, status: 'paid' },
    { name: 'CloudVision Corp', revenue: 8200, invoices: 2, status: 'pending' },
    { name: 'InnovateHub', revenue: 6700, invoices: 3, status: 'paid' },
    { name: 'DevForward Inc', revenue: 5400, invoices: 2, status: 'paid' }
  ];

  const revenueByService = [
    { service: 'Web Development', revenue: 18500, percentage: 39 },
    { service: 'API Development', revenue: 12200, percentage: 26 },
    { service: 'Cloud Infrastructure', revenue: 9800, percentage: 21 },
    { service: 'Consulting', revenue: 6750, percentage: 14 }
  ];

  return (
    <Box>
      {/* Header */}
      <Box mb={8}>
        <Heading size="lg" mb={2} color="white">Revenue Analytics</Heading>
        <Text color="gray.300">Comprehensive financial insights and revenue analysis</Text>
      </Box>

      {/* Revenue Overview Cards */}
      <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }} gap={6} mb={8}>
        <RevenueCard
          title="Total Revenue"
          value={revenueData.totalRevenue}
          change={revenueData.monthlyGrowth}
          changeType="positive"
          icon={DollarSign}
          colorScheme="green"
        />
        <RevenueCard
          title="Paid Invoices"
          value={revenueData.paidInvoices}
          change="8.2%"
          changeType="positive"
          icon={FileText}
          colorScheme="blue"
        />
        <RevenueCard
          title="Pending Revenue"
          value={revenueData.pendingInvoices}
          change="15.3%"
          changeType="positive"
          icon={TrendingUp}
          colorScheme="purple"
        />
        <RevenueCard
          title="Average Invoice"
          value={revenueData.averageInvoice}
          change="5.7%"
          changeType="positive"
          icon={Target}
          colorScheme="orange"
        />
        <RevenueCard
          title="Active Clients"
          value={revenueData.clientCount.toString()}
          change="2 new"
          changeType="positive"
          icon={Calendar}
          colorScheme="teal"
        />
        <RevenueCard
          title="Monthly Goal"
          value="$50,000"
          change="94.5%"
          changeType="positive"
          icon={BarChart3}
          colorScheme="pink"
        />
      </Grid>

      {/* Revenue Analysis Grid */}
      <Grid templateColumns={{ base: '1fr', lg: '2fr 1fr' }} gap={6} mb={8}>
        {/* Monthly Breakdown */}
        <Card bg="gray.800" borderColor="gray.700">
          <CardBody>
            <Heading size="md" mb={6} color="white">Monthly Revenue Trend</Heading>
            <VStack spacing={4} align="stretch">
              {monthlyBreakdown.map((month) => (
                <Box key={month.month}>
                  <HStack justify="space-between" mb={2}>
                    <HStack>
                      <Text fontWeight="medium" color="white" minW="50px">
                        {month.month}
                      </Text>
                      <Text fontSize="sm" color="gray.400">
                        {month.invoices} invoices
                      </Text>
                    </HStack>
                    <Text fontWeight="bold" color="green.400">
                      ${month.revenue.toLocaleString()}
                    </Text>
                  </HStack>
                  <Progress 
                    value={(month.revenue / 50000) * 100} 
                    colorScheme="green" 
                    bg="gray.700"
                    borderRadius="full"
                  />
                </Box>
              ))}
            </VStack>
          </CardBody>
        </Card>

        {/* Revenue by Service */}
        <Card bg="gray.800" borderColor="gray.700">
          <CardBody>
            <Heading size="md" mb={6} color="white">Revenue by Service</Heading>
            <VStack spacing={4} align="stretch">
              {revenueByService.map((service) => (
                <Box key={service.service}>
                  <HStack justify="space-between" mb={2}>
                    <Text fontSize="sm" color="gray.300" fontWeight="medium">
                      {service.service}
                    </Text>
                    <Text fontSize="sm" fontWeight="bold" color="white">
                      ${service.revenue.toLocaleString()}
                    </Text>
                  </HStack>
                  <HStack mb={2}>
                    <Progress 
                      value={service.percentage} 
                      colorScheme={
                        service.service === 'Web Development' ? 'blue' :
                        service.service === 'API Development' ? 'purple' :
                        service.service === 'Cloud Infrastructure' ? 'green' : 'orange'
                      }
                      bg="gray.700"
                      borderRadius="full"
                      flex={1}
                    />
                    <Text fontSize="xs" color="gray.500" minW="35px">
                      {service.percentage}%
                    </Text>
                  </HStack>
                </Box>
              ))}
            </VStack>
          </CardBody>
        </Card>
      </Grid>

      {/* Top Clients */}
      <Card bg="gray.800" borderColor="gray.700">
        <CardBody>
          <Heading size="md" mb={6} color="white">Top Revenue Clients</Heading>
          <VStack spacing={4} align="stretch">
            {topClients.map((client, index) => (
              <Box key={client.name}>
                <HStack justify="space-between" align="start">
                  <VStack align="start" spacing={1} flex={1}>
                    <HStack>
                      <Text fontWeight="medium" color="white">
                        #{index + 1} {client.name}
                      </Text>
                      <Badge
                        colorScheme={client.status === 'paid' ? 'green' : 'orange'}
                        variant="subtle"
                      >
                        {client.status}
                      </Badge>
                    </HStack>
                    <Text fontSize="sm" color="gray.400">
                      {client.invoices} invoices
                    </Text>
                  </VStack>
                  <Text fontWeight="bold" color="green.400">
                    ${client.revenue.toLocaleString()}
                  </Text>
                </HStack>
                {index !== topClients.length - 1 && <Divider mt={4} borderColor="gray.700" />}
              </Box>
            ))}
          </VStack>
        </CardBody>
      </Card>
    </Box>
  );
};

export default Revenue;